import {
  classifyOrgAccessState,
  type OrgAccessState,
  type UiErrorLike,
} from "@lifter/hub-ui/browser-shared";
import { Loader2, RefreshCcw, ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { HubApiError } from "../api";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/ui/page-header";
import { Panel, PanelBody } from "../components/ui/panel";
import { PortalOrgProvider, useCurrentOrgQuery } from "../org/context";
import { useBrandedDocumentTitle } from "./instance-branding";

interface AuthShellProps {
  children: ReactNode;
}

/**
 * Workspace-level gate. By the time this renders the user is already signed
 * in — `AuthGate` settles authentication off `GET /api/me` before the portal
 * mounts. `AuthShell` only classifies the outcome of `GET /api/org/current`
 * ("not ready" while the runtime is still coming up, "unavailable" for any
 * other failure) and otherwise renders the portal.
 */
export function AuthShell({ children }: AuthShellProps) {
  const currentOrg = useCurrentOrgQuery();

  if (currentOrg.isPending) {
    return (
      <ShellStateFrame>
        <Loader2
          aria-hidden="true"
          className="size-5 animate-spin text-[var(--color-text-tertiary)]"
        />
        <PageHeader title="Loading workspace" />
      </ShellStateFrame>
    );
  }

  if (currentOrg.isError) {
    const state = classifyOrgAccessState(toUiErrorLike(currentOrg.error));
    return <AuthRejectionScreen onRetry={() => currentOrg.refetch()} state={state} />;
  }

  return <PortalOrgProvider current={currentOrg.data}>{children}</PortalOrgProvider>;
}

function AuthRejectionScreen({ state, onRetry }: { state: OrgAccessState; onRetry: () => void }) {
  const copy = authCopyForState(state);
  // The portal never mounts from here, so this screen names the tab for as
  // long as it is on screen.
  useBrandedDocumentTitle(copy.title);

  return (
    <ShellStateFrame>
      <ShieldAlert aria-hidden="true" className="size-7 text-[var(--color-warning)]" />
      <PageHeader title={copy.title} />

      <div className="flex flex-wrap justify-center gap-3">
        <Button variant="secondary" onClick={onRetry}>
          <RefreshCcw aria-hidden="true" className="size-4" />
          Retry
        </Button>
      </div>
    </ShellStateFrame>
  );
}

function ShellStateFrame({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-[var(--color-page-bg)] px-6 py-10 text-center text-[var(--color-text)]">
      <Panel className="w-full max-w-lg">
        <PanelBody className="flex flex-col items-center gap-5">{children}</PanelBody>
      </Panel>
    </main>
  );
}

function authCopyForState(state: OrgAccessState): {
  title: string;
} {
  switch (state) {
    case "not_ready":
      return {
        title: "Workspace setup isn't complete.",
      };
    default:
      return {
        title: "Couldn't load the workspace",
      };
  }
}

function toUiErrorLike(error: unknown): UiErrorLike {
  if (error instanceof HubApiError) {
    return { status: error.status, errorCode: error.errorCode };
  }
  return {};
}
